import { useState, type FormEvent } from "react";
import { apiPost } from "@/lib/apiClient";

export type ActivityTouchpoint = {
  id: string;
  accountId: string;
  contactId?: string | null;
  channel: "email" | "sms" | "linkedin" | "call" | "note" | "system";
  direction: "inbound" | "outbound" | "internal";
  outcome?: string | null;
  content?: string | null;
  createdBy?: string | null;
  createdAt: string;
};

export type ActivityContactOption = {
  id: string;
  label: string;
};

const CHANNELS = [
  { value: "call", label: "Call" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "note", label: "Note" },
] as const;

const DIRECTIONS = [
  { value: "outbound", label: "Outbound" },
  { value: "inbound", label: "Inbound" },
  { value: "internal", label: "Internal" },
] as const;

const OUTCOMES = [
  { value: "", label: "No outcome" },
  { value: "connected", label: "Connected" },
  { value: "left_voicemail", label: "Left voicemail" },
  { value: "no_answer", label: "No answer" },
  { value: "replied", label: "Replied" },
  { value: "meeting_booked", label: "Meeting booked" },
  { value: "interested", label: "Interested" },
  { value: "not_interested", label: "Not interested" },
  { value: "opt_out", label: "Opt out" },
  { value: "do_not_contact", label: "Do not contact" },
] as const;

type CrmActivityPanelProps = {
  accountId: string;
  initialTouchpoints: ActivityTouchpoint[];
  contacts?: ActivityContactOption[];
  defaultContactId?: string | null;
  filterToContact?: boolean;
  title?: string;
};

function channelLabel(channel: ActivityTouchpoint["channel"]) {
  return CHANNELS.find((c) => c.value === channel)?.label || channel;
}

function directionLabel(direction: ActivityTouchpoint["direction"]) {
  return DIRECTIONS.find((d) => d.value === direction)?.label || direction;
}

function outcomeLabel(outcome: string | null | undefined) {
  if (!outcome) return null;
  return OUTCOMES.find((o) => o.value === outcome)?.label || outcome.replace(/_/g, " ");
}

function contactLabel(contactId: string | null | undefined, contacts: ActivityContactOption[]) {
  if (!contactId) return null;
  return contacts.find((c) => c.id === contactId)?.label || contactId;
}

export function CrmActivityPanel({
  accountId,
  initialTouchpoints,
  contacts = [],
  defaultContactId = null,
  filterToContact = false,
  title = "Activity",
}: CrmActivityPanelProps) {
  const [touchpoints, setTouchpoints] = useState(initialTouchpoints);
  const [channel, setChannel] = useState<(typeof CHANNELS)[number]["value"]>("call");
  const [direction, setDirection] = useState<(typeof DIRECTIONS)[number]["value"]>("outbound");
  const [outcome, setOutcome] = useState("");
  const [content, setContent] = useState("");
  const [contactId, setContactId] = useState(defaultContactId || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const visibleTouchpoints =
    filterToContact && defaultContactId
      ? touchpoints.filter((tp) => !tp.contactId || tp.contactId === defaultContactId)
      : touchpoints;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const res = await apiPost<ActivityTouchpoint>("/api/crm/touchpoints", {
      accountId,
      contactId: contactId || null,
      channel,
      direction,
      outcome: outcome || null,
      content: content.trim() || null,
    });
    setSaving(false);
    if (!res.ok || !res.data) {
      setError(res.error || "Unable to log activity.");
      return;
    }
    setTouchpoints((prev) => [res.data as ActivityTouchpoint, ...prev]);
    setContent("");
    setOutcome("");
    setFormOpen(false);
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-white/5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">Log calls, emails, and notes for this account.</p>
        </div>
        <button
          type="button"
          onClick={() => setFormOpen((open) => !open)}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"
        >
          {formOpen ? "Cancel" : "Log activity"}
        </button>
      </div>

      {formOpen ? (
        <form onSubmit={(e) => void onSubmit(e)} className="mt-4 space-y-3 border-t border-gray-200 pt-4 dark:border-white/10">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block text-sm">
              <span className="mb-1 block text-gray-500 dark:text-gray-400">Channel</span>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value as (typeof CHANNELS)[number]["value"])}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-white/20 dark:bg-black/20"
                required
              >
                {CHANNELS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-gray-500 dark:text-gray-400">Direction</span>
              <select
                value={direction}
                onChange={(e) => setDirection(e.target.value as (typeof DIRECTIONS)[number]["value"])}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-white/20 dark:bg-black/20"
                required
              >
                {DIRECTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-gray-500 dark:text-gray-400">Outcome</span>
              <select
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-white/20 dark:bg-black/20"
              >
                {OUTCOMES.map((option) => (
                  <option key={option.value || "none"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {contacts.length > 0 ? (
              <label className="block text-sm">
                <span className="mb-1 block text-gray-500 dark:text-gray-400">Contact</span>
                <select
                  value={contactId}
                  onChange={(e) => setContactId(e.target.value)}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-white/20 dark:bg-black/20"
                >
                  <option value="">Account only</option>
                  {contacts.map((contact) => (
                    <option key={contact.id} value={contact.id}>
                      {contact.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
          <label className="block text-sm">
            <span className="mb-1 block text-gray-500 dark:text-gray-400">Notes</span>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={3}
              placeholder="What happened?"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-white/20 dark:bg-black/20"
            />
          </label>
          {error ? (
            <p className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-700/60 dark:bg-red-900/20 dark:text-red-200">
              {error}
            </p>
          ) : null}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save activity"}
            </button>
          </div>
        </form>
      ) : null}

      <div className="mt-4 space-y-3 border-t border-gray-200 pt-4 dark:border-white/10">
        {visibleTouchpoints.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No activity logged yet. Use Log activity to record the first touchpoint.
          </p>
        ) : (
          visibleTouchpoints.map((tp) => {
            const contact = contactLabel(tp.contactId, contacts);
            const outcomeText = outcomeLabel(tp.outcome);
            return (
              <article key={tp.id} className="rounded-md border border-gray-200 px-3 py-3 dark:border-white/10">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold">
                    {channelLabel(tp.channel)} · {directionLabel(tp.direction)}
                    {outcomeText ? (
                      <span className="font-normal text-gray-600 dark:text-gray-300"> · {outcomeText}</span>
                    ) : null}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {tp.createdAt ? new Date(tp.createdAt).toLocaleString() : "—"}
                  </p>
                </div>
                {tp.content ? (
                  <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-200">{tp.content}</p>
                ) : null}
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {contact ? `Contact: ${contact}` : "Account activity"}
                  {tp.createdBy ? ` · ${tp.createdBy}` : null}
                </p>
              </article>
            );
          })
        )}
      </div>
    </section>
  );
}
